import React, { useRef, useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { DefaultSeo } from "next-seo";
import dynamic from "next/dynamic";
import SEO from "../next-seo.config";
import Header from "components/Header";

import Layout from "@components/Layout";
import { DebuggerTools } from "components/DebugTools";

import { HeaderContext, CartContext, UserContext } from "contexts/";
import { useLocalStorage, useLogRocket, useScrollRestoration, useOnScreen } from "hooks";
import { checkLocalStoreForStaging, promptForPassword } from "utils/helpers";

import userApi from "utils/api/UserApi";
import productsApi from "utils/api/ProductsApi";
import GTM from "utils/GTM";
import SearchApi from "utils/api/SearchApi";

// import Footer from "components/Footer";
const Footer = dynamic(() => import("./Footer"), {
	loading: () => <header />,
});

export default function App(props) {
	const { Component, pageProps, router } = props;

	const notificationDialogue = pageProps?.data?.items?.[0]?.fields?.notificationDialog;

	const { getAccessTokenSilently, loginWithRedirect, user, isAuthenticated, isLoading } = useAuth0();
	const { initLogRocket, identify } = useLogRocket();

	// console.log({ user, isAuthenticated, isLoading });
	const route = router.asPath || location?.pathname;

	const isLandingPage = route ? route.includes("/lp/") : false;

	const [localCart, setLocalCart] = useLocalStorage(process.env.CART_LOCAL_STORAGE_NAME, {});
	const footerRef = useRef();
	const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
	const [userData, setUserData] = useState(null);
	const [profile, setProfile] = useState({});
	const [bookmarks, setBookmarks] = useState([]);
	const [loadingBookmarks, setLoadingBookmarks] = useState(false);
	const [productSearchTerms, setProductSearchTerms] = useState([]);
	const [bookProductSearchTerms, setBookProductSearchTerms] = useState([]);
	const certificationsRef = useRef();
	//handle with user skeleton loading state
	const [fetchingUser, setFetchingUser] = useState(isLoading);
	const [fetchingProfile, setFetchingProfile] = useState(true);
	const [aaplStagePass, setAaplStagePass] = useState(checkLocalStoreForStaging());
	const [shopAccountUrl, setShopAccountUrl] = useState(null);
	const [triedGetToken, setTriedGetToken] = useState(false);

	const membershipStatusRef = useRef();
	const userCoursesRef = useRef();

	//FOR NON-PRODUCTION
	const [pageId, setPageId] = useState(null);

	const cartItems = Object.keys(localCart || {}).reduce((acc, variantId) => {
		acc.push(localCart[variantId]);
		return acc;
	}, []);

	useScrollRestoration(router);

	useEffect(() => {
		GTM.pageView(route);
	}, [router.asPath]);

	useEffect(() => {
		setFetchingUser(isLoading);
	}, [isLoading]);

	async function tryGetToken() {
		try {
			setTriedGetToken(true);

			const token = await getAccessTokenSilently();
		} catch (err) {
			console.log("not logged in");
		}
	}

	useEffect(() => {
		if (isLoading) {
			setFetchingUser(true);
		} else if (!isLoading) {
			setFetchingUser(false);
		}
		if (!user && !isAuthenticated && !triedGetToken) {
			tryGetToken();
		}

		if (user && !isLoading && isAuthenticated) {
			userFunctions();
		}
	}, [user, isAuthenticated, isLoading, triedGetToken]);

	useEffect(() => {
		async function fetchProductSearchTerms() {
			try {
				const productSearchTerms = await SearchApi.productSearchTerms();
				const bookProductSearchTerms = await SearchApi.bookProductSearchTerms();

				setProductSearchTerms(productSearchTerms);

				setBookProductSearchTerms(bookProductSearchTerms);
			} catch (err) {
				console.error(err);
			}
		}
		GTM.init();
		if (process.env.APP_ENV === "production") {
			initLogRocket();
		}
		fetchProductSearchTerms();
	}, []);

	useEffect(() => {
		if (aaplStagePass) return;
		if (process.env.APP_ENV !== "production") {
			const aaplStagePass = checkLocalStoreForStaging();
			setAaplStagePass(aaplStagePass);
		} else {
			setAaplStagePass(true);
		}
	}, [aaplStagePass]);

	async function userFunctions() {
		console.log("Running user functions");
		setFetchingUser(false);
		identify(user);
		setFetchingProfile(true);
		const token = await getAccessTokenSilently();

		const userData = await userApi.getUserData(
			user["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/contactid"],
			token
		);
		setFetchingProfile(false);

		if (!userData) return; //this shouldn't really ever happen except in dev w/o server running

		userData.id = user["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/userid"];

		//parse discount
		if (userData.defaultPriceLevelId) {
			const str = userData.defaultPriceLevelId;
			const regex = /\d+/;
			const result = str.match(regex)?.[0];

			if (!isNaN(result)) {
				userData.discount = (100 - parseFloat(result)) / 100;
			}
		}

		//THESE ARE FOR TESTING< PLEASE REMOVE
		//REMOVE
		// userData.isMember = true;
		// userData.isVanguard = true;
		// -------------------------------------------
		setUserData(userData);
		//get users shop account url
		const userAccountUrl = await userApi.getMultipass(`${process.env.SHOPIFY_STOREFRONT_DOMAIN}/account`, token);
		setShopAccountUrl(userAccountUrl);
		// console.log(user, userData);
		setLoadingBookmarks(true);
		const bookmarks = await userApi.getBookmarks(userData, "article", token);
		setBookmarks(bookmarks);
		setLoadingBookmarks(false);
		GTM.setUserData(userData);
		const membershipStatus = await productsApi.getMembershipStatus(token);
		membershipStatusRef.current = membershipStatus;
		const userCourses = await userApi.getUserCourses(token);
		// console.log(userCourses);
		userCoursesRef.current = userCourses;
		const certs = await userApi.getCerts(token);
		// console.log(certs);
		certificationsRef.current = certs?.certifications;
	}

	if (!aaplStagePass) {
		promptForPassword(setAaplStagePass);
		return <></>;
	}

	return (
		<UserContext.Provider
			value={{
				bookmarks,
				setBookmarks,
				loadingBookmarks,
				setLoadingBookmarks,
				certificationsRef,
				userCoursesRef,
				fetchingProfile,
				fetchingUser,
				membershipStatusRef,
				profile,
				setFetchingProfile,
				setFetchingUser,
				setProfile,
				userData,
				setUserData,
				user,
				shopAccountUrl,
				userApi,
			}}
		>
			<CartContext.Provider value={{ localCart, setLocalCart, cartItems }}>
				<HeaderContext.Provider
					value={{
						isMobileNavOpen,
						setIsMobileNavOpen,
						footerRef,
						productSearchTerms,
						bookProductSearchTerms,
						pageId,
						setPageId,
						notificationDialogue,
					}}
				>
					<DebuggerTools />

					<Layout>
						<DefaultSeo {...SEO} />
						{!isLandingPage && <Header />}

						<div className={!isMobileNavOpen ? `myShow` : "d-none"}>
							<Component key={"component"} {...pageProps} />
							<Footer footerRef={footerRef} key={"footer"} />
						</div>
					</Layout>
				</HeaderContext.Provider>
			</CartContext.Provider>
		</UserContext.Provider>
	);
}
